import React, { useEffect, useRef } from 'react';

import { Button, Flex, Text, TextInput, createStyles } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const useStyles = createStyles((theme) => ({
  toolbar: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    backgroundColor: 'white',
    borderTopLeftRadius: theme.radius.md,
    borderTopRightRadius: theme.radius.md,
    padding: '12px 12px'
  },
  navigationButton: { '&:disabled': { opacity: 0.5 } }
}));

export function NavigationControls({
  currentPage,
  numPages,
  onPageChange
}: {
  currentPage: number;
  numPages: number;
  onPageChange: (page: number) => void;
}) {
  const { classes } = useStyles();
  const { t } = useTranslation();

  // Keep track of the input element to remove focus once page is selected
  const inputRef = useRef<HTMLInputElement>(null);
  const pageToShowForm = useForm({
    initialValues: { pageNumber: currentPage.toString() }
  });

  useEffect(() => {
    pageToShowForm.setValues({ pageNumber: currentPage.toString() });
  }, [currentPage]);

  const handlePageSubmit = () => {
    const pageNumber = parseInt(pageToShowForm.values.pageNumber, 10);
    if (!isNaN(pageNumber)) {
      // Clamp the value between 1 and numPages
      const clampedPage = Math.min(Math.max(pageNumber, 1), numPages);
      onPageChange(clampedPage);
      pageToShowForm.setValues({ pageNumber: clampedPage.toString() });
    } else {
      // If invalid number, reset to current page
      pageToShowForm.setValues({ pageNumber: currentPage.toString() });
    }

    // Remove focus from input
    inputRef.current?.blur();
  };

  const handleKeyDown = (keydownEvent: React.KeyboardEvent<HTMLInputElement>) => {
    if (keydownEvent.key === 'Enter') {
      keydownEvent.preventDefault();
      handlePageSubmit();
    }
  };

  return (
    <Flex align="center" justify="space-between" className={classes.toolbar}>
      <Button
        className={classes.navigationButton}
        variant="white"
        color="gray"
        disabled={currentPage <= 1}
        leftIcon={<IconChevronLeft />}
        onClick={() => onPageChange(currentPage - 1)}>
        {t('PDF_VIEWER.NAVIGATION.PREVIOUS')}
      </Button>

      <Flex gap="xs" align="center" justify="space-between">
        <Text size="sm" color="dimmed" weight={500}>
          {t('PDF_VIEWER.NAVIGATION.PAGE')}
        </Text>

        <TextInput
          w="50px"
          sx={(theme) => ({
            input: {
              textAlign: 'center',
              fontSize: '15px',
              backgroundColor: ` ${theme.colors.gray[0]}`
            }
          })}
          {...pageToShowForm.getInputProps('pageNumber')}
          onKeyDown={handleKeyDown}
          size="xs"
          ref={inputRef}
        />

        <Text size="sm" color="dimmed" weight={500}>
          {t('PDF_VIEWER.NAVIGATION.OF')} <b>{numPages}</b>
        </Text>
      </Flex>

      <Button
        className={classes.navigationButton}
        variant="white"
        color="gray"
        disabled={currentPage >= numPages}
        rightIcon={<IconChevronRight />}
        onClick={() => onPageChange(currentPage + 1)}>
        {t('PDF_VIEWER.NAVIGATION.NEXT')}
      </Button>
    </Flex>
  );
}
